import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { videos, formatViews } from '@/mocks/videos';

const SITE_ORIGIN = 'http://saitemold.com';

export default function VideoDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const video = useMemo(() => videos.find((v) => v.id === id), [id]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.id !== video.id && v.categorySlug === video.categorySlug)
      .slice(0, 4);
  }, [video]);

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">
            {t('videoDetail.notFound')}
          </h1>
          <Link
            to="/"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            {t('notFound.backHome')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_ORIGIN}/video/${video.id}`,
        url: `${SITE_ORIGIN}/video/${video.id}`,
        name: `${video.title} - ${t('siteName')}`,
        description: video.description,
        isPartOf: {
          '@id': `${SITE_ORIGIN}/#website`,
        },
        breadcrumb: {
          '@id': `${SITE_ORIGIN}/video/${video.id}#breadcrumb`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_ORIGIN}/video/${video.id}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: t('breadcrumb.home'),
            item: `${SITE_ORIGIN}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: video.category,
            item: `${SITE_ORIGIN}/category/${video.categorySlug}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: video.title,
          },
        ],
      },
      {
        '@type': 'VideoObject',
        name: video.title,
        description: video.description,
        thumbnailUrl: video.thumbnail,
        duration: `PT${video.duration.replace(':', 'M')}S`,
        uploadDate: video.publishedAt,
        author: {
          '@type': 'Person',
          name: video.creator,
        },
        interactionStatistic: [
          {
            '@type': 'InteractionCounter',
            interactionType: { '@type': 'WatchAction' },
            userInteractionCount: video.views,
          },
          {
            '@type': 'InteractionCounter',
            interactionType: { '@type': 'LikeAction' },
            userInteractionCount: video.likes,
          },
        ],
      },
      {
        '@type': 'Article',
        headline: video.title,
        description: video.description,
        image: video.thumbnail,
        datePublished: video.publishedAt,
        author: {
          '@type': 'Person',
          name: video.creator,
        },
        publisher: {
          '@type': 'Organization',
          name: t('siteName'),
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <div className="pt-20 pb-8 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <li>
              <Link to="/" className="hover:text-foreground-700 transition-colors whitespace-nowrap">
                {t('breadcrumb.home')}
              </Link>
            </li>
            <li>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
            </li>
            <li>
              <Link
                to={`/category/${video.categorySlug}`}
                className="hover:text-foreground-700 transition-colors whitespace-nowrap"
              >
                {video.category}
              </Link>
            </li>
            <li>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
            </li>
            <li className="text-foreground-900 font-medium line-clamp-1">{video.title}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Video Player Placeholder */}
            <div className="relative aspect-video bg-foreground-900 rounded-xl overflow-hidden mb-6">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <button
                  type="button"
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-background-50/90 flex items-center justify-center hover:bg-background-50 transition-colors cursor-pointer"
                  aria-label={t('videoDetail.play')}
                >
                  <i className="ri-play-fill text-2xl md:text-3xl text-primary-500 ml-1" />
                </button>
              </div>
            </div>

            <article>
              <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line" />
                  </span>
                  {formatViews(video.views)}{t('video.views')}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-heart-line" />
                  </span>
                  {formatViews(video.likes)}{t('video.likes')}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                  {video.duration}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  {video.publishedAt}
                </span>
              </div>

              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-background-200/70">
                <img
                  src={video.creatorAvatar}
                  alt={video.creator}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-900">
                    {video.creator}
                  </p>
                  <p className="text-xs text-foreground-500">{t('video.creator')}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-2">
                  {t('videoDetail.description')}
                </h2>
                <p className="text-foreground-600 leading-relaxed">
                  {video.description}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${video.categorySlug}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </article>
          </div>

          {/* Sidebar - Related Videos */}
          <aside className="lg:col-span-1">
            <h2 className="text-base font-semibold text-foreground-900 mb-4">
              {t('video.related')}
            </h2>
            <div className="space-y-4">
              {relatedVideos.map((rv) => (
                <Link
                  key={rv.id}
                  to={`/video/${rv.id}`}
                  className="flex gap-3 group"
                >
                  <div className="relative w-32 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={rv.thumbnail}
                      alt={rv.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-1 right-1 bg-black/70 text-background-50 text-xs px-1.5 py-0.5 rounded">
                      {rv.duration}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                      {rv.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{rv.creator}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">
                      {formatViews(rv.views)}{t('videoDetail.plays')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}
